/**
 * DesireSuggestions — horizontal carousel of members who share your desires.
 * Rendered on the Feed above the composer so users see fresh discovery every
 * time they open the app. Falls back to `null` if the viewer has no desires
 * set (nothing to compute compatibility from).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sparkles, Plus, Check } from "lucide-react";

export default function DesireSuggestions() {
  const [rows, setRows] = useState(null);
  const [following, setFollowing] = useState(new Set());

  useEffect(() => {
    api.get("/users/suggestions/desires", { params: { limit: 12 } })
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]));
  }, []);

  const follow = async (handle) => {
    try {
      const { data } = await api.post(`/users/${handle}/follow`);
      setFollowing((s) => new Set([...s, handle]));
      if ((data?.shared_desires_count || 0) >= 3) {
        toast.success(`✨ Following @${handle} — ${data.shared_desires_count} shared desires`);
      } else {
        toast.success(`Following @${handle}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not follow.");
    }
  };

  if (rows === null) return null;
  if (rows.length === 0) return null;

  return (
    <div className="card-surface p-4 mb-5" data-testid="desire-suggestions">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-[0.3em] text-rose-500 flex items-center gap-1.5">
          <Sparkles size={12} /> People who share your desires
        </div>
        <Link to="/nearby" className="text-[10px] text-rose-400 hover:text-rose-300 uppercase tracking-widest">
          See all
        </Link>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar" data-testid="desire-suggestions-row">
        {rows.map((u) => {
          const isFollowing = following.has(u.id);
          return (
            <div
              key={u.id}
              className="shrink-0 w-32 flex flex-col items-center text-center"
              data-testid={`desire-suggestion-${u.id}`}
            >
              <Link to={`/u/${u.id}`} className="relative">
                <Avatar className={`w-16 h-16 border border-white/10`}>
                  <AvatarImage src={u.avatar_url} />
                  <AvatarFallback className="bg-rose-600 text-white text-lg">
                    {u.display_name?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span
                  className="absolute -bottom-1 -right-1 text-[9px] uppercase tracking-widest bg-rose-500 text-white rounded-full px-1.5 py-0.5"
                  title={`${u.shared_desires_count} shared desires`}
                >
                  ✨ {u.shared_desires_count}
                </span>
              </Link>
              <Link
                to={`/u/${u.id}`}
                className={`mt-2 text-xs truncate max-w-full font-medium`}
              >
                {u.display_name}
              </Link>
              <button
                type="button"
                onClick={() => !isFollowing && follow(u.id)}
                disabled={isFollowing}
                data-testid={`desire-suggestion-follow-${u.id}`}
                className={`mt-2 w-full inline-flex items-center justify-center gap-1 rounded-full text-[10px] uppercase tracking-widest font-semibold py-1.5 transition-colors ${
                  isFollowing
                    ? "bg-transparent border border-rose-500/50 text-rose-400"
                    : "bg-rose-600 hover:bg-rose-500 text-white"
                }`}
              >
                {isFollowing ? <><Check size={10} /> Following</> : <><Plus size={10} /> Follow</>}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
